/**
 * 控制台里的【人】—— 2026-08-27 起是真的了。
 *
 * ★ 这个文件原来写着"还没有用户体系, hub 现在的身份只有两种:一把 admin 令牌
 *   + 每设备一把", 并且**故意**不列假账号(那个决定是对的)。passkey 上线后那段话
 *   变成了假话, 而假话比空页面贵:它会让人不去用一个已经存在的能力。
 *
 * 现在这里画的每一行都来自 hub 的 /auth/people, 没有一个是编的。
 *
 * 为什么吊销 passkey 和停用人是**两个**动作, 不合并成一个:
 *   · 吊销一把 passkey = "这台电脑丢了", 人还在, 他别的设备照常。
 *   · 停用一个人      = "这个人不该再有权限了", 他所有会话当场断掉。
 *   合并成一个按钮的话, 想做前者的人会做成后者, 而这两件事的代价差得很远。
 */
import { useState } from 'react';
import { usePeople } from '@/hooks/useHubData';
import { LoadError, NoDataSource } from '@/components/feature/DataSource';
import { timeAgoSec } from '@/lib/format';

export default function UserPermissions() {
  const { people, credentials, loading, error, unavailable, refetch,
    revokeCredential, disablePerson } = usePeople();
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  if (unavailable) {
    return (
      <NoDataSource
        title="passkey 登录未启用"
        reason={unavailable}
        phase="给 hub 配上 --auth-db 与 OXMAN_HUB_RP_ID / OXMAN_HUB_RP_ORIGINS"
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs font-bold text-foreground-800">人与 passkey</h3>
          <p className="text-[11px] text-foreground-400 mt-0.5">
            每人一个独立身份,审计里记的就是这个名字。新人注册要一次性注册码,
            <span className="font-mono"> 只能在私有网里 curl 取</span>。
          </p>
        </div>
        <button onClick={refetch} className="text-[11px] text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap">
          <i className={`ri-refresh-line mr-1 ${loading ? 'animate-spin' : ''}`} />刷新
        </button>
      </div>

      {error && <LoadError error={error} onRetry={refetch} />}
      {actionError && <LoadError error={actionError} />}

      {people.length === 0 && !loading ? (
        <div className="text-xs text-foreground-400 py-8 text-center leading-relaxed">
          还没有人注册。在一台已入网的机器上取一个注册码:
          <pre className="mt-2 inline-block text-left font-mono text-[10px] bg-background-100 rounded px-3 py-2 overflow-x-auto">
            {`curl -X POST http://100.64.0.1:35481/api/v1/auth/enroll-code \\\n  -d '{"name":"你的名字"}'`}
          </pre>
        </div>
      ) : (
        <ul className="divide-y divide-background-200/60">
          {people.map((p) => {
            // 只显示这个人没被吊销的那些 passkey;吊销过的留在库里是为了审计,
            // 不是为了摆在台面上。
            const keys = credentials.filter((c) => c.personId === p.id && !c.revokedAt);
            const revoked = credentials.filter((c) => c.personId === p.id && c.revokedAt).length;
            const off = !!p.disabledAt;
            return (
              <li key={p.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-medium ${off ? 'text-foreground-400 line-through' : 'text-foreground-800'}`}>{p.name}</span>
                      <span className="font-mono text-[10px] text-foreground-400">human:{p.name}</span>
                      {off && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">已停用</span>}
                    </div>
                    <div className="text-[10px] text-foreground-400 mt-0.5">
                      加入于 {new Date(p.createdAt * 1000).toLocaleDateString('zh-CN')}
                      {revoked > 0 && <span> · 历史吊销 {revoked} 把</span>}
                    </div>
                  </div>
                  {!off && (
                    <button
                      disabled={busy === p.id}
                      onClick={() => run(p.id, () => disablePerson(p.id))}
                      className="shrink-0 text-[11px] px-2.5 py-1 rounded-md border border-red-500/30 text-red-600 hover:bg-red-500/10 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                      title="停用这个人:他的所有会话当场断掉,所有 passkey 一起失效"
                    >
                      {busy === p.id ? '停用中…' : '停用此人'}
                    </button>
                  )}
                </div>

                {keys.length === 0 ? (
                  // 一个人可以没有 passkey —— 全被吊销之后就是这样。说出来,
                  // 别让它看起来像"这一行没加载出来"。
                  <p className="mt-2 ml-1 text-[10px] text-foreground-400">没有可用的 passkey(全部已吊销),他现在进不来。</p>
                ) : (
                  <ul className="mt-2 ml-1 space-y-1">
                    {keys.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-3 text-[11px]">
                        <div className="min-w-0 flex items-center gap-2">
                          <i className="ri-key-2-line text-foreground-400" />
                          <span className="text-foreground-700 truncate">{c.name || '未命名设备'}</span>
                          <span className="text-foreground-400 whitespace-nowrap">
                            {c.lastUsed ? `最近使用 ${timeAgoSec(c.lastUsed)}` : '从未使用过'}
                          </span>
                        </div>
                        <button
                          disabled={busy === c.id}
                          onClick={() => run(c.id, () => revokeCredential(c.id))}
                          className="shrink-0 text-foreground-500 hover:text-red-600 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                          title="只吊销这一把 passkey:这台设备丢了,人还在"
                        >
                          {busy === c.id ? '吊销中…' : '吊销'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-background-100 rounded-lg p-4">
          <h4 className="text-xs font-bold text-foreground-800 mb-1">人(passkey 会话)</h4>
          <p className="text-[11px] text-foreground-500 leading-relaxed">
            可注册/轮换/吊销设备、建任务、确认告警、看审计。私钥在本人电脑的
            Secure Enclave 里,hub 只有公钥 —— 拖库也登不进来。
          </p>
        </div>
        <div className="bg-background-100 rounded-lg p-4">
          <h4 className="text-xs font-bold text-foreground-800 mb-1">设备(每台一把)</h4>
          <p className="text-[11px] text-foreground-500 leading-relaxed">
            可上报遥测、入网、认领与完成工作单元。<b>不能</b>管别的设备、不能建任务、
            不能看审计,也不能看这一页。
          </p>
        </div>
      </div>
    </div>
  );
}
